/* Cart subscription upgrade — UpCart-style "Subscribe & save" button per line item.
 *
 * Pattern: in cart-drawer.liquid, each line item that has selling plans available
 * (and isn't already a subscription) renders a button with data attributes:
 *   data-line-index, data-variant-id, data-quantity
 *
 * Frequency selector (Lauren punch-list item 5, 2026-05-21):
 *   - Single plan  → button also carries data-selling-plan-id (original path).
 *   - Multiple plans → button carries data-freq-select (ID of the sibling
 *     <select>). On click we read the currently-selected option from that
 *     select to get the plan ID. This means the customer's picker choice is
 *     always honored — no hardcoded fallback after DOM construction.
 *
 * On click: POST /cart/change.js with the line + selling_plan param to swap the
 * line from one-time to recurring. After success, refresh the drawer's section
 * (Dawn pattern) so the line shows the new subscription state, the upgrade
 * button disappears, and the total reflects the discount.
 */
(function () {
  'use strict';

  function refreshCartDrawer() {
    /* Use Shopify's section rendering API to fetch a fresh cart-drawer
       and replace the current element. Source is the merchant's own theme
       render endpoint — not untrusted user input. We use replaceWith() with
       parsed DOM nodes (no innerHTML) to avoid any XSS surface and to let
       custom-element lifecycle re-fire connectedCallback on the new node. */
    const sectionId = 'cart-drawer';
    return fetch(`/?sections=${sectionId}`)
      .then((r) => r.json())
      .then((sections) => {
        const html = sections[sectionId];
        if (!html) throw new Error('section render returned no html');
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newDrawer = doc.querySelector('cart-drawer');
        const oldDrawer = document.querySelector('cart-drawer');
        if (!newDrawer || !oldDrawer) {
          throw new Error('cart-drawer element missing in old or new DOM');
        }
        const wasActive = oldDrawer.classList.contains('active');
        const adopted = document.adoptNode(newDrawer);
        oldDrawer.replaceWith(adopted);
        if (wasActive) adopted.classList.add('active');
        return true;
      })
      .catch((err) => {
        console.warn('cart-subscription-upgrade: refresh failed, reloading', err);
        window.location.reload();
        return false;
      });
  }

  function showError(button, message) {
    const original = button.textContent;
    button.disabled = true;
    button.textContent = message;
    setTimeout(() => {
      button.disabled = false;
      button.textContent = original;
    }, 3000);
  }

  /**
   * Resolve the selling plan ID for a given upgrade button.
   *
   * Two cases:
   *   1. Single-plan products: button has data-selling-plan-id set directly
   *      in Liquid (original path, no select rendered).
   *   2. Multi-plan products: button has data-freq-select pointing to the ID
   *      of the sibling <select>. Read the selected value at click time so the
   *      customer's choice is always honored.
   *
   * If neither attribute is present (shouldn't happen, but defensive) return
   * null so the caller can bail out early.
   */
  function resolveSellingPlanId(button) {
    // Path 1: single-plan button (data-selling-plan-id on the button itself).
    if (button.dataset.sellingPlanId) {
      return button.dataset.sellingPlanId;
    }

    // Path 2: multi-plan button (read from sibling <select>).
    const selectId = button.dataset.freqSelect;
    if (selectId) {
      const select = document.getElementById(selectId);
      if (select && select.value) {
        return select.value;
      }
    }

    return null;
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('.cart-item__upgrade-button');
    if (!button) return;

    event.preventDefault();
    if (button.disabled) return;

    const line = parseInt(button.dataset.lineIndex, 10);
    const sellingPlanId = resolveSellingPlanId(button);
    const quantity = parseInt(button.dataset.quantity, 10);

    if (!line || !sellingPlanId || !quantity) {
      console.warn('cart-subscription-upgrade: missing required data', {
        line,
        sellingPlanId,
        quantity,
        dataset: button.dataset
      });
      return;
    }

    button.disabled = true;
    button.textContent = 'Upgrading…';

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          line: line,
          quantity: quantity,
          selling_plan: sellingPlanId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('cart-subscription-upgrade: change failed', response.status, errorText);
        showError(button, 'Try again');
        return;
      }

      await refreshCartDrawer();
      document.dispatchEvent(new CustomEvent('cart:refreshed', { bubbles: true }));
    } catch (err) {
      console.error('cart-subscription-upgrade: exception', err);
      showError(button, 'Try again');
    }
  });
})();
